import { useEffect, useRef, useState } from "react";

export interface MenuAction {
  label: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}

// A fixed-position popup menu at (x,y); closes on outside click or Escape. Used for row "⋯"
// buttons and right-click context menus across the shell.
export function ActionMenu({ x, y, actions, onClose }: { x: number; y: number; actions: MenuAction[]; onClose: () => void }) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const close = (e: Event) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", close);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);
  // Keep the menu on-screen near the right/bottom edges.
  const left = Math.min(x, (typeof window !== "undefined" ? window.innerWidth : 9999) - 200);
  const top = Math.min(y, (typeof window !== "undefined" ? window.innerHeight : 9999) - (actions.length * 30 + 12));
  return (
    <div
      ref={ref}
      style={{ top: Math.max(4, top), left: Math.max(4, left) }}
      className="fixed z-[60] min-w-44 overflow-hidden rounded-md border border-border bg-panel py-1 text-xs shadow-2xl"
    >
      {actions.length === 0 && <div className="px-3 py-1.5 text-muted">—</div>}
      {actions.map((a, i) => (
        <button
          key={i}
          disabled={a.disabled}
          onClick={() => {
            onClose();
            a.onClick();
          }}
          className={`block w-full px-3 py-1.5 text-left hover:bg-panel2 disabled:opacity-40 ${a.danger ? "text-danger" : ""}`}
        >
          {a.label}
        </button>
      ))}
    </div>
  );
}

// A trigger button that opens an ActionMenu anchored under it.
export function Dropdown({
  label,
  title,
  actions,
  className,
}: {
  label: string;
  title?: string;
  actions: MenuAction[];
  className?: string;
}) {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  return (
    <>
      <button
        title={title}
        onClick={(e) => {
          const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
          setPos(pos ? null : { x: r.left, y: r.bottom + 2 });
        }}
        className={className ?? "rounded border border-border px-2 py-1 text-xs hover:border-accent"}
      >
        {label}
      </button>
      {pos && <ActionMenu x={pos.x} y={pos.y} actions={actions} onClose={() => setPos(null)} />}
    </>
  );
}
